import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Cesium from 'cesium';
import { X, GripHorizontal, GripVertical, ExternalLink, ChevronLeft, ChevronRight, Loader2, BarChart3, Pencil } from 'lucide-react';
import { elevationService } from '../services/elevationService';
import { polygonStatsService } from '../services/polygonStatsService';
import { ElevationProfile } from './MeasureTool/ElevationProfile';
import { interpolateAlongCoordinatePolyline } from '../utils/polylineInterpolation';
import { METERS_TO_FEET, formatDistance, formatElevation, formatAreaFromDistanceUnit, convertElevationBreakdownItems } from '../utils/units';
import { useDialogResize } from '../hooks/useDialogResize';
import { DrawnBy } from './shared/DrawnBy';
const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};
const calculatePolygonArea = (vertices) => {
    if (vertices.length < 3)
        return 0;
    const R = 6371000;
    let total = 0;
    for (let i = 0; i < vertices.length; i++) {
        const j = (i + 1) % vertices.length;
        const lat1 = vertices[i].lat * Math.PI / 180;
        const lat2 = vertices[j].lat * Math.PI / 180;
        const dLng = (vertices[j].lng - vertices[i].lng) * Math.PI / 180;
        total += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
    }
    return Math.abs(total * R * R / 2);
};
const PieChart = ({ items, size = 70 }) => {
    const radius = size / 2;
    const innerRadius = radius * 0.55;
    const center = radius;
    const total = items.reduce((sum, item) => sum + item.percent, 0);
    if (total === 0)
        return null;
    let currentAngle = -Math.PI / 2;
    const arcs = items.filter(item => item.percent > 0).map((item) => {
        const sliceAngle = (item.percent / total) * 2 * Math.PI;
        const startAngle = currentAngle;
        const endAngle = currentAngle + sliceAngle;
        currentAngle = endAngle;
        const x1 = center + radius * Math.cos(startAngle);
        const y1 = center + radius * Math.sin(startAngle);
        const x2 = center + radius * Math.cos(endAngle);
        const y2 = center + radius * Math.sin(endAngle);
        const ix1 = center + innerRadius * Math.cos(startAngle);
        const iy1 = center + innerRadius * Math.sin(startAngle);
        const ix2 = center + innerRadius * Math.cos(endAngle);
        const iy2 = center + innerRadius * Math.sin(endAngle);
        const largeArc = sliceAngle > Math.PI ? 1 : 0;
        const d = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;
        return { d, color: item.color || 'var(--pewter)', key: item.key };
    });
    return (_jsx("svg", { width: size, height: size, viewBox: `0 0 ${size} ${size}`, children: arcs.map(arc => _jsx("path", { d: arc.d, fill: arc.color }, arc.key)) }));
};
const StatBreakdownDisplay = ({ title, items }) => {
    if (items.length === 0)
        return null;
    return (_jsxs("div", { className: "mb-3", children: [_jsx("div", { className: "text-xs font-medium mb-1.5", style: { color: 'var(--dew)' }, children: title }), _jsxs("div", { className: "flex gap-3", children: [_jsx(PieChart, { items: items, size: 64 }), _jsx("div", { className: "flex-1 min-w-0", children: items.map((item) => (_jsxs("div", { className: "flex items-center gap-2 mb-0.5", children: [_jsx("div", { className: "w-2 h-2 rounded-full flex-shrink-0", style: { backgroundColor: item.color || 'var(--pewter)' } }), _jsx("span", { className: "text-[10px] flex-1 truncate", style: { color: 'var(--sand)' }, children: item.title }), _jsxs("span", { className: "text-[10px] font-medium", style: { color: 'var(--dew)' }, children: [item.percent.toFixed(1), "%"] })] }, item.key))) })] })] }));
};
export const ServerArtifactDialog = ({ artifact, screenPosition, projectId, onClose, onPopOut, isPoppedOut = false, onHighlightSegment, onHighlightEdge, onHoverPosition, distanceUnit: externalDistanceUnit, onChangeUnit: externalOnChangeUnit, onEdit, canModify = true, }) => {
    var _a, _b;
    const dialogRef = useRef(null);
    const dialogWidth = 320;
    const padding = 16;
    const [position, setPosition] = useState(() => {
        let left = screenPosition.x + 10;
        let top = Math.max(padding, screenPosition.y - 200);
        if (left + dialogWidth + padding > window.innerWidth)
            left = screenPosition.x - dialogWidth - 10;
        if (left < padding)
            left = padding;
        if (top < padding)
            top = padding;
        return { x: left, y: top };
    });
    const { width, startLeftResize, startRightResize } = useDialogResize({
        initialWidth: dialogWidth,
        minWidth: 320,
        position,
        setPosition,
    });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const [elevationProfile, setElevationProfile] = useState(undefined);
    const [pointElevation, setPointElevation] = useState(undefined);
    const [edgeProfiles, setEdgeProfiles] = useState([]);
    const [currentEdge, setCurrentEdge] = useState(0);
    const [segmentProfiles, setSegmentProfiles] = useState([]);
    const [currentSegment, setCurrentSegment] = useState('total');
    const [statistics, setStatistics] = useState(null);
    const [statisticsLoading, setStatisticsLoading] = useState(false);
    const [unit, setUnit] = useState(externalDistanceUnit || 'ft');
    const isImperial = unit === 'ft';
    const geometry = useMemo(() => JSON.parse(artifact.geometryJson), [artifact.geometryJson]);
    useEffect(() => {
        if (isPoppedOut)
            return;
        const handleClickOutside = (event) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, isPoppedOut]);
    useEffect(() => {
        if (!onHighlightSegment || artifact.artifactType !== 'path')
            return;
        const segmentIndex = currentSegment === 'total' ? null : currentSegment;
        onHighlightSegment(artifact.id, segmentIndex);
        return () => {
            onHighlightSegment(artifact.id, null);
        };
    }, [currentSegment, artifact.id, artifact.artifactType, onHighlightSegment]);
    useEffect(() => {
        if (!onHighlightEdge || artifact.artifactType !== 'polygon')
            return;
        onHighlightEdge(artifact.id, currentEdge);
        return () => {
            onHighlightEdge(artifact.id, null);
        };
    }, [currentEdge, artifact.id, artifact.artifactType, onHighlightEdge]);
    const handleCalculateStatistics = useCallback(async () => {
        if (!projectId || artifact.artifactType !== 'polygon')
            return;
        setStatisticsLoading(true);
        try {
            const vertices = geometry.vertices;
            const coordinates = vertices.map(v => [v.lng, v.lat]);
            coordinates.push(coordinates[0]);
            const geojson = JSON.stringify({
                type: 'Polygon',
                coordinates: [coordinates]
            });
            const area = calculatePolygonArea(vertices);
            const result = await polygonStatsService.calculatePolygonStatistics({
                projectId,
                polygonGeojson: geojson,
                areaM2: area
            });
            setStatistics(result);
        }
        catch (err) {
            console.error('Failed to calculate statistics:', err);
        }
        finally {
            setStatisticsLoading(false);
        }
    }, [projectId, artifact.artifactType, geometry]);
    const handleMouseDown = useCallback((e) => {
        if (e.target.closest('.drag-handle')) {
            setIsDragging(true);
            dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
            e.preventDefault();
        }
    }, [position]);
    useEffect(() => {
        if (!isDragging)
            return;
        const handleMouseMove = (e) => {
            const newX = Math.max(0, Math.min(window.innerWidth - dialogWidth, e.clientX - dragStartRef.current.x));
            const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragStartRef.current.y));
            setPosition({ x: newX, y: newY });
        };
        const handleMouseUp = () => setIsDragging(false);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);
    useEffect(() => {
        var _a, _b;
        if (!projectId) {
            setElevationProfile(null);
            setPointElevation(null);
            return;
        }
        if (artifact.artifactType === 'measurement' && geometry.startLat && geometry.endLat) {
            elevationService.getLineElevationProfile(projectId, geometry.startLat, geometry.startLng, geometry.endLat, geometry.endLng, 30).then(profile => setElevationProfile(profile || null))
                .catch(() => setElevationProfile(null));
        }
        else if (artifact.artifactType === 'path' && ((_a = geometry.points) === null || _a === void 0 ? void 0 : _a.length) >= 2) {
            const points = geometry.points;
            const segmentCount = points.length - 1;
            const initialSegments = points.slice(0, -1).map((p, i) => {
                const next = points[i + 1];
                return {
                    profile: undefined,
                    distance: calculateDistance(p.lat, p.lng, next.lat, next.lng)
                };
            });
            setSegmentProfiles(initialSegments);
            setCurrentSegment('total');
            // Total profile: one server call along the actual polyline (follows every vertex).
            // Scale samples with segment count so resolution doesn't degrade on long paths.
            elevationService.getPolylineElevationProfile(projectId, points.map(p => ({ lat: p.lat, lng: p.lng })), Math.max(50, segmentCount * 30)).then(profile => setElevationProfile(profile || null))
                .catch(() => setElevationProfile(null));
            // Per-segment profiles for the segment selector view.
            for (let i = 0; i < segmentCount; i++) {
                const segIdx = i;
                elevationService.getLineElevationProfile(projectId, points[i].lat, points[i].lng, points[i + 1].lat, points[i + 1].lng, 30).then(profile => {
                    setSegmentProfiles(prev => {
                        const updated = [...prev];
                        if (updated[segIdx])
                            updated[segIdx] = { ...updated[segIdx], profile: profile || null };
                        return updated;
                    });
                }).catch(() => {
                    setSegmentProfiles(prev => {
                        const updated = [...prev];
                        if (updated[segIdx])
                            updated[segIdx] = { ...updated[segIdx], profile: null };
                        return updated;
                    });
                });
            }
        }
        else if (artifact.artifactType === 'placemark' && geometry.lat && geometry.lng) {
            elevationService.sampleElevation(projectId, geometry.lat, geometry.lng)
                .then(elev => setPointElevation(elev))
                .catch(() => setPointElevation(null));
        }
        else if (artifact.artifactType === 'polygon' && ((_b = geometry.vertices) === null || _b === void 0 ? void 0 : _b.length) >= 3) {
            const vertices = geometry.vertices;
            const initialEdges = vertices.map((v, i) => {
                const j = (i + 1) % vertices.length;
                return { profile: undefined, distance: calculateDistance(v.lat, v.lng, vertices[j].lat, vertices[j].lng) };
            });
            setEdgeProfiles(initialEdges);
            setCurrentEdge(0);
            for (let i = 0; i < vertices.length; i++) {
                const j = (i + 1) % vertices.length;
                const edgeIdx = i;
                elevationService.getLineElevationProfile(projectId, vertices[i].lat, vertices[i].lng, vertices[j].lat, vertices[j].lng, 20).then(profile => {
                    setEdgeProfiles(prev => {
                        const updated = [...prev];
                        if (updated[edgeIdx]) {
                            updated[edgeIdx] = { ...updated[edgeIdx], profile: profile || null };
                        }
                        return updated;
                    });
                }).catch(() => {
                    setEdgeProfiles(prev => {
                        const updated = [...prev];
                        if (updated[edgeIdx]) {
                            updated[edgeIdx] = { ...updated[edgeIdx], profile: null };
                        }
                        return updated;
                    });
                });
            }
        }
        else {
            setElevationProfile(null);
            setPointElevation(null);
        }
    }, [projectId, artifact.id, artifact.artifactType, geometry]);
    const lineCoordinatesForProfile = useMemo(() => {
        var _a, _b;
        if (artifact.artifactType === 'measurement' && geometry.startLat && geometry.endLat) {
            return [
                { lat: geometry.startLat, lng: geometry.startLng },
                { lat: geometry.endLat, lng: geometry.endLng }
            ];
        }
        if (artifact.artifactType === 'path' && ((_a = geometry.points) === null || _a === void 0 ? void 0 : _a.length) >= 2) {
            if (currentSegment !== 'total' && segmentProfiles[currentSegment]) {
                const points = geometry.points;
                return [points[currentSegment], points[currentSegment + 1]];
            }
            return geometry.points;
        }
        if (artifact.artifactType === 'polygon' && ((_b = geometry.vertices) === null || _b === void 0 ? void 0 : _b.length) >= 3 && edgeProfiles.length > 0) {
            const vertices = geometry.vertices;
            const j = (currentEdge + 1) % vertices.length;
            return [vertices[currentEdge], vertices[j]];
        }
        return [];
    }, [artifact.artifactType, geometry, currentSegment, segmentProfiles, currentEdge, edgeProfiles]);
    const handleProfileHoverFraction = useCallback((fraction) => {
        if (!onHoverPosition)
            return;
        if (fraction === null || lineCoordinatesForProfile.length < 2) {
            onHoverPosition(null, artifact.color);
            return;
        }
        const coord = interpolateAlongCoordinatePolyline(lineCoordinatesForProfile, fraction);
        if (coord) {
            const pos = Cesium.Cartesian3.fromDegrees(coord.lng, coord.lat);
            onHoverPosition(pos, artifact.color);
        }
        else {
            onHoverPosition(null, artifact.color);
        }
    }, [onHoverPosition, lineCoordinatesForProfile, artifact.color]);
    const handleUnitChange = (newUnit) => {
        setUnit(newUnit);
        externalOnChangeUnit === null || externalOnChangeUnit === void 0 ? void 0 : externalOnChangeUnit(newUnit);
    };
    const getTypeLabel = () => {
        switch (artifact.artifactType) {
            case 'measurement': return 'Measurement';
            case 'path': return 'Path';
            case 'placemark': return 'Placemark';
            case 'polygon': return 'Polygon';
            case 'contour_highlight': return 'Contour';
            case 'stream_highlight': return 'Stream';
            default: return artifact.artifactType;
        }
    };
    const getDistanceOrArea = () => {
        var _a, _b, _c, _d, _e;
        if (artifact.artifactType === 'measurement') {
            const dist = calculateDistance(geometry.startLat, geometry.startLng, geometry.endLat, geometry.endLng);
            return formatDistance(dist, unit);
        }
        if (artifact.artifactType === 'path' && ((_a = geometry.points) === null || _a === void 0 ? void 0 : _a.length) >= 2) {
            let total = 0;
            for (let i = 0; i < geometry.points.length - 1; i++) {
                total += calculateDistance(geometry.points[i].lat, geometry.points[i].lng, geometry.points[i + 1].lat, geometry.points[i + 1].lng);
            }
            return formatDistance(total, unit);
        }
        if (artifact.artifactType === 'polygon' && ((_b = geometry.vertices) === null || _b === void 0 ? void 0 : _b.length) >= 3) {
            const area = calculatePolygonArea(geometry.vertices);
            return formatAreaFromDistanceUnit(area, unit);
        }
        if (artifact.artifactType === 'stream_highlight') {
            let allCoords = [];
            if (geometry.is_multi_segment && ((_c = geometry.segments) === null || _c === void 0 ? void 0 : _c.length) > 0) {
                for (const seg of geometry.segments) {
                    if (((_d = seg.lineCoordinates) === null || _d === void 0 ? void 0 : _d.length) > 0) {
                        allCoords = allCoords.concat(seg.lineCoordinates);
                    }
                }
            }
            else if (((_e = geometry.line_coordinates) === null || _e === void 0 ? void 0 : _e.length) > 0) {
                allCoords = geometry.line_coordinates;
            }
            if (allCoords.length >= 2) {
                let total = 0;
                for (let i = 0; i < allCoords.length - 1; i++) {
                    total += calculateDistance(allCoords[i].lat, allCoords[i].lng, allCoords[i + 1].lat, allCoords[i + 1].lng);
                }
                return formatDistance(total, unit);
            }
        }
        return null;
    };
    const distanceOrArea = getDistanceOrArea();
    // Length of the highlighted contour slice, in metres. The stored
    // coordinates are the FULL source contour; the highlighted length is the
    // slice. Fractions are length-normalized, so the slice length is the
    // fraction span × total (wrap-aware for closed loops).
    const contourLengthMeters = (() => {
        if (artifact.artifactType !== 'contour_highlight')
            return null;
        if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length < 2)
            return null;
        const coords = geometry.coordinates;
        let total = 0;
        for (let i = 0; i < coords.length - 1; i++) {
            total += calculateDistance(coords[i].lat, coords[i].lng, coords[i + 1].lat, coords[i + 1].lng);
        }
        const sf = typeof geometry.start_fraction === 'number' ? geometry.start_fraction : 0;
        const ef = typeof geometry.end_fraction === 'number' ? geometry.end_fraction : 1;
        const span = sf <= ef ? (ef - sf) : (1 - sf + ef);
        return total * span;
    })();
    // Elevation of a saved contour highlight, normalized to METRES so the unit
    // toggle can render it consistently with the length. The stored value's
    // native unit comes from its suffix ("328 ft" → ft); bare or "m" is metres.
    const contourElevationMeters = (() => {
        var _a, _b;
        if (artifact.artifactType !== 'contour_highlight')
            return null;
        const raw = String((_a = geometry.elevation) !== null && _a !== void 0 ? _a : '').trim();
        const m = raw.match(/^(-?[\d.]+)\s*(ft|m)?$/i);
        if (!m)
            return null;
        const val = parseFloat(m[1]);
        if (isNaN(val))
            return null;
        return ((_b = m[2]) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === 'ft' ? val * (1 / METERS_TO_FEET) : val;
    })();
    // Path artifacts are now shown via the merged PathOptionsDialog in view mode
    // (handled in useServerArtifactRenderer's onPathArtifactClick divert). Render
    // nothing here as a defensive guard if a path ever reaches this code path.
    if (artifact.artifactType === 'path')
        return null;
    const handlePopOut = () => {
        if (onPopOut) {
            onPopOut(artifact, { x: position.x + 50, y: position.y });
        }
    };
    return (_jsxs("div", { ref: dialogRef, className: "fixed z-[65] rounded-lg shadow-xl", style: {
            left: position.x,
            top: position.y,
            width,
            backgroundColor: 'var(--charcoal)',
            border: '1px solid var(--pewter)',
            maxHeight: `calc(100vh - ${position.y + padding}px)`,
            display: 'flex',
            flexDirection: 'column',
            userSelect: isDragging ? 'none' : 'auto'
        }, children: [_jsxs("div", { className: "drag-handle flex items-center justify-between px-3 py-2 cursor-move rounded-t-lg", style: { backgroundColor: 'var(--pewter)', flexShrink: 0 }, onMouseDown: handleMouseDown, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(GripHorizontal, { size: 14, style: { color: 'var(--dew)', opacity: 0.5 } }), _jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--sand)' }, children: getTypeLabel() })] }), _jsx("button", { onClick: onClose, className: "p-1 rounded hover:bg-opacity-20 hover:bg-gray-500 transition-colors", style: { color: 'var(--dew)' }, onMouseDown: (e) => e.stopPropagation(), children: _jsx(X, { size: 16 }) })] }), _jsxs("div", { className: "p-4", style: { overflowY: 'auto', flex: 1, minHeight: 0 }, children: [_jsx("div", { className: "mb-3", children: _jsx("div", { className: "text-sm font-medium", style: { color: 'var(--sand)' }, children: artifact.name }) }), _jsx(DrawnBy, { author: artifact.author }), distanceOrArea && (_jsxs("div", { className: "flex justify-between items-start gap-2 mb-4", children: [_jsx("div", { className: "text-lg font-bold leading-tight", style: { color: 'var(--dew)' }, children: distanceOrArea }), artifact.artifactType !== 'polygon' && (_jsxs("select", { value: unit, onChange: (e) => handleUnitChange(e.target.value), className: "px-2 py-1 rounded text-xs cursor-pointer", style: {
                                    backgroundColor: 'var(--pewter)',
                                    color: 'var(--sand)',
                                    border: '1px solid var(--charcoal)',
                                    minWidth: 86,
                                }, title: "Display unit", children: [_jsx("option", { value: "m", children: "Meters" }), _jsx("option", { value: "km", children: "Kilometers" }), _jsx("option", { value: "ft", children: "Feet" }), _jsx("option", { value: "yd", children: "Yards" }), _jsx("option", { value: "mi", children: "Miles" })] }))] })), artifact.artifactType === 'placemark' && (_jsxs("div", { className: "mb-4 pb-4 border-b", style: { borderColor: 'var(--pewter)' }, children: [_jsx("div", { className: "text-xs mb-1", style: { color: 'var(--pewter)' }, children: "Elevation" }), pointElevation === undefined ? (_jsx("div", { className: "text-sm", style: { color: 'var(--dew)' }, children: "Loading..." })) : pointElevation === null ? (_jsx("div", { className: "text-sm", style: { color: 'var(--pewter)' }, children: "No elevation data" })) : (_jsx("div", { className: "text-lg font-bold", style: { color: 'var(--dew)' }, children: unit === 'ft'
                                    ? `${(pointElevation * METERS_TO_FEET).toFixed(1)} ft`
                                    : `${pointElevation.toFixed(1)} m` })), _jsxs("div", { className: "text-xs mt-1", style: { color: 'var(--pewter)' }, children: [(_a = geometry.lat) === null || _a === void 0 ? void 0 : _a.toFixed(6), ", ", (_b = geometry.lng) === null || _b === void 0 ? void 0 : _b.toFixed(6)] })] })), artifact.artifactType === 'contour_highlight' && (contourLengthMeters !== null || contourElevationMeters !== null) && (_jsxs("div", { className: "mb-4 pb-4 border-b flex flex-col gap-1", style: { borderColor: 'var(--pewter)' }, children: [contourLengthMeters !== null && (_jsxs("div", { className: "flex justify-between items-baseline gap-2", children: [_jsxs("div", { className: "flex items-baseline gap-2 min-w-0", children: [_jsx("span", { className: "text-[10px] uppercase tracking-wide flex-shrink-0", style: { color: 'var(--dew)' }, children: "Length" }), _jsx("span", { className: "text-sm font-semibold whitespace-nowrap", style: { color: 'var(--sand)' }, children: formatDistance(contourLengthMeters, unit) })] }), _jsxs("select", { value: unit, onChange: (e) => handleUnitChange(e.target.value), className: "px-2 py-1 rounded text-xs cursor-pointer flex-shrink-0", style: {
                                            backgroundColor: 'var(--pewter)',
                                            color: 'var(--sand)',
                                            border: '1px solid var(--charcoal)',
                                            minWidth: 86,
                                        }, title: "Display unit", children: [_jsx("option", { value: "m", children: "Meters" }), _jsx("option", { value: "km", children: "Kilometers" }), _jsx("option", { value: "ft", children: "Feet" }), _jsx("option", { value: "yd", children: "Yards" }), _jsx("option", { value: "mi", children: "Miles" })] })] })), contourElevationMeters !== null && (_jsxs("div", { className: "flex items-baseline gap-2", children: [_jsx("span", { className: "text-[10px] uppercase tracking-wide flex-shrink-0", style: { color: 'var(--dew)' }, children: "Elevation" }), _jsx("span", { className: "text-sm font-semibold whitespace-nowrap", style: { color: 'var(--sand)' }, children: formatElevation(contourElevationMeters, unit) })] }))] })), artifact.artifactType === 'measurement' && (_jsxs("div", { className: "mb-4 pb-4 border-b", style: { borderColor: 'var(--pewter)' }, children: [_jsx(ElevationProfile, { elevationProfile: elevationProfile, unit: unit, color: artifact.color, onUnitChange: handleUnitChange, onHoverFraction: onHoverPosition && lineCoordinatesForProfile.length >= 2 ? handleProfileHoverFraction : undefined }), !isPoppedOut && onPopOut && elevationProfile && (_jsxs("button", { onClick: handlePopOut, className: "w-full mt-2 py-1.5 px-3 rounded text-xs flex items-center justify-center gap-2 transition-colors", style: { backgroundColor: 'var(--pewter)', color: 'var(--sand)' }, children: [_jsx(ExternalLink, { size: 12 }), "Pop Out Profile"] }))] })), artifact.artifactType === 'polygon' && projectId && (_jsxs("div", { className: "mb-4", children: [!statistics && !statisticsLoading && (_jsxs("button", { onClick: handleCalculateStatistics, className: "w-full py-2 px-3 rounded flex items-center justify-center gap-2 transition-colors text-sm", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: [_jsx(BarChart3, { size: 16 }), "Get Statistics"] })), statisticsLoading && (_jsxs("div", { className: "flex items-center justify-center gap-2 py-3", style: { color: 'var(--dew)' }, children: [_jsx(Loader2, { size: 16, className: "animate-spin" }), _jsx("span", { className: "text-sm", children: "Calculating..." })] })), statistics && (_jsxs("div", { className: "p-3 rounded", style: { backgroundColor: 'var(--pewter)' }, children: [_jsx("div", { className: "text-xs font-semibold mb-2", style: { color: 'var(--sand)' }, children: "Statistics" }), _jsxs("div", { className: "grid grid-cols-2 gap-x-4 gap-y-1 mb-3 text-xs", children: [_jsx("div", { style: { color: 'var(--dew)' }, children: "Max Elevation" }), _jsx("div", { style: { color: 'var(--sand)' }, children: formatElevation(statistics.maxHeightM, isImperial ? 'ft' : 'm') }), _jsx("div", { style: { color: 'var(--dew)' }, children: "Min Elevation" }), _jsx("div", { style: { color: 'var(--sand)' }, children: formatElevation(statistics.minHeightM, isImperial ? 'ft' : 'm') }), _jsx("div", { style: { color: 'var(--dew)' }, children: "Std Dev" }), _jsx("div", { style: { color: 'var(--sand)' }, children: formatElevation(statistics.altitudeStdDev, isImperial ? 'ft' : 'm') })] }), _jsx(StatBreakdownDisplay, { title: "Aspect", items: statistics.aspectBreakdown }), _jsx(StatBreakdownDisplay, { title: "Slope", items: statistics.slopeBreakdown }), _jsx(StatBreakdownDisplay, { title: "Elevation", items: convertElevationBreakdownItems(statistics.elevationBreakdown, isImperial) }), _jsx(StatBreakdownDisplay, { title: "Geomorphons", items: statistics.geomorphonsBreakdown })] }))] })), artifact.artifactType === 'polygon' && edgeProfiles.length > 0 && (_jsxs("div", { className: "mb-4 pb-4 border-b", style: { borderColor: 'var(--pewter)' }, children: [_jsxs("div", { className: "flex items-center justify-between mb-2", children: [_jsx("div", { className: "text-xs font-medium", style: { color: 'var(--dew)' }, children: "Edge Profiles" }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("button", { onClick: () => setCurrentEdge(prev => (prev - 1 + edgeProfiles.length) % edgeProfiles.length), className: "p-0.5 rounded transition-colors", style: { color: 'var(--dew)' }, children: _jsx(ChevronLeft, { size: 14 }) }), _jsxs("span", { className: "text-xs", style: { color: 'var(--sand)' }, children: [currentEdge + 1, " / ", edgeProfiles.length] }), _jsx("button", { onClick: () => setCurrentEdge(prev => (prev + 1) % edgeProfiles.length), className: "p-0.5 rounded transition-colors", style: { color: 'var(--dew)' }, children: _jsx(ChevronRight, { size: 14 }) })] })] }), _jsx("div", { className: "text-xs mb-1", style: { color: 'var(--sand)' }, children: formatDistance(edgeProfiles[currentEdge].distance, unit) }), edgeProfiles[currentEdge].profile ? (_jsx(ElevationProfile, { elevationProfile: edgeProfiles[currentEdge].profile, unit: unit, color: typeof geometry.outlineColor === 'string' ? geometry.outlineColor : artifact.color, onHoverFraction: onHoverPosition && lineCoordinatesForProfile.length >= 2 ? handleProfileHoverFraction : undefined })) : edgeProfiles[currentEdge].profile === undefined ? (_jsxs("div", { className: "flex items-center justify-center py-3 text-xs", style: { color: 'var(--pewter)' }, children: [_jsx(Loader2, { size: 12, className: "animate-spin mr-1" }), "Loading profile..."] })) : (_jsx("div", { className: "text-xs text-center py-2", style: { color: 'var(--pewter)' }, children: "No elevation data" }))] })), onEdit && !isPoppedOut && canModify && (_jsxs("button", { onClick: onEdit, className: "w-full py-2 px-3 rounded flex items-center justify-center gap-2 transition-colors text-sm", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: [_jsx(Pencil, { size: 14 }), "Edit"] }))] }), _jsx("div", { onMouseDown: startLeftResize, title: "Drag to resize width", style: { position: 'absolute', left: 0, bottom: 0, width: 16, height: 16, cursor: 'ew-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(GripVertical, { size: 12, style: { color: 'var(--dew)', opacity: 0.6 } }) }), _jsx("div", { onMouseDown: startRightResize, title: "Drag to resize width", style: { position: 'absolute', right: 0, bottom: 0, width: 16, height: 16, cursor: 'ew-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(GripVertical, { size: 12, style: { color: 'var(--dew)', opacity: 0.6 } }) })] }));
};
